import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Icons } from '../utils/icons';
import api, { GOOGLE_CLIENT_ID, BOT_USERNAME } from '../utils/api';
import { fontStack, glassCard, inputStyle, btnPrimary, btnGhost, errorBox, successBox } from '../styles/shared';
import CustomSelect from './CustomSelect';

// Password validation rules
const PASSWORD_RULES = [
    { label: '8+ characters', test: (p) => p.length >= 8 },
    { label: 'Lowercase (a-z)', test: (p) => /[a-z]/.test(p) },
    { label: 'Uppercase (A-Z)', test: (p) => /[A-Z]/.test(p) },
    { label: 'Symbol (!@#$...)', test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(p) },
];

const validatePassword = (pwd) => PASSWORD_RULES.filter(r => !r.test(pwd)).map(r => r.label);

// Login page styles (dashboard theme)
const pageWrap = { minHeight: '100dvh', fontFamily: fontStack, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box', position: 'relative' };
const cardSt = { ...glassCard, width: '100%', maxWidth: '400px', textAlign: 'center' };
const inputBox = { position: 'relative', width: '100%', height: '55px', marginBottom: '20px' };
const inputIcon = { position: 'absolute', top: '50%', right: '18px', transform: 'translateY(-50%)', fontSize: '18px', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };

// Landing-themed signup styles
const L = {
    text: '#0f1729',
    textMuted: '#94a3b8',
    textSecondary: '#475569',
    blue: '#00AEEF',
    blueHover: '#0098d4',
    border: '#e2e8f0',
    bgAlt: '#f8fafc',
    success: '#10b981',
    error: '#ef4444',
};

const signupPageWrap = {
    minHeight: '100dvh', fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', boxSizing: 'border-box', position: 'relative',
    background: '#fff',
};

const signupCardSt = {
    width: '100%', maxWidth: '420px', textAlign: 'center',
    background: '#fff', borderRadius: '20px', padding: '40px 36px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
    border: `1px solid ${L.border}`,
};

const signupInputSt = {
    width: '100%', padding: '14px 45px 14px 18px',
    border: `1.5px solid ${L.border}`, borderRadius: '12px',
    background: '#fff', color: L.text, fontSize: '15px',
    boxSizing: 'border-box', fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    outline: 'none', transition: 'all 0.2s ease',
};

const signupInputIcon = {
    position: 'absolute', top: '50%', right: '16px', transform: 'translateY(-50%)',
    fontSize: '18px', color: L.textMuted, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const signupBtn = {
    width: '100%', padding: '14px 24px', background: L.blue, color: '#fff',
    border: 'none', borderRadius: '100px', fontSize: '15px', fontWeight: 600,
    cursor: 'pointer', fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    transition: 'all 0.25s ease', boxShadow: '0 2px 8px rgba(0,174,239,0.25)',
};

const signupBtnSecondary = {
    padding: '10px 20px', background: '#fff', color: L.text,
    border: `1.5px solid ${L.border}`, borderRadius: '100px',
    fontSize: '14px', fontWeight: 500, cursor: 'pointer',
    fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    transition: 'all 0.25s ease',
};

const signupErrorBox = {
    padding: '12px 16px', background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.2)', borderRadius: '12px',
    color: L.error, marginBottom: '20px', fontSize: '14px',
};

function GoogleSignInButton({ onClick, loading, landing }) {
    if (!GOOGLE_CLIENT_ID) return null;
    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0', color: landing ? L.textMuted : 'var(--text-muted)', fontSize: '13px' }}>
                <div style={{ flex: 1, height: '1px', background: landing ? L.border : 'var(--border)' }} />
                <span style={{ padding: '0 12px' }}>or</span>
                <div style={{ flex: 1, height: '1px', background: landing ? L.border : 'var(--border)' }} />
            </div>
            <button type="button" onClick={onClick} disabled={loading} style={{
                width: '100%', padding: '14px 20px',
                background: landing ? '#fff' : 'var(--btn-secondary-bg)',
                border: landing ? `1.5px solid ${L.border}` : '1px solid var(--btn-secondary-border)',
                borderRadius: landing ? '100px' : '30px',
                color: landing ? L.text : 'var(--btn-secondary-text)',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '20px',
                fontFamily: landing ? "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" : fontStack,
                transition: 'all 0.2s ease',
            }}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
            </button>
        </>
    );
}

export default function LoginPage() {
    const { isAuth, login, token, user, isChatter } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (!isAuth) return;
        // Chatter with pending onboarding — stay on login page for onboarding
        if (isChatter && user?.onboarding_status === 'pending') {
            setChatterToken(token);
            setChatterUser(user);
            setIsLogin(false);
            setRegistrationStep(4);
            setOnboardingStep('choose');
            return;
        }
        // Creator onboarding in progress — don't redirect
        const savedStep = sessionStorage.getItem('creatorOnboarding');
        if (savedStep) {
            const step = parseInt(savedStep, 10);
            if (step >= 5) {
                setIsLogin(false);
                setRegistrationStep(step);
                return;
            }
        }
        sessionStorage.removeItem('creatorOnboarding');
        navigate('/dashboard');
    }, [isAuth, navigate, isChatter, user, token]);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLogin, setIsLogin] = useState(() => !searchParams.get('signup'));
    const [registrationStep, setRegistrationStep] = useState(1);
    const [showForgot, setShowForgot] = useState(false);
    const [showResetForm, setShowResetForm] = useState(false);
    const [forgotUsername, setForgotUsername] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [resetNewPassword, setResetNewPassword] = useState('');
    const [resetConfirmPassword, setResetConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    // Chatter onboarding state
    const [onboardingStep, setOnboardingStep] = useState(''); // choose | connect | codeEntry | requestCreator | requestSent | profile
    const [creatorConnectCode, setCreatorConnectCode] = useState('');
    const [creatorTgConnected, setCreatorTgConnected] = useState(false);
    const [chatterToken, setChatterToken] = useState(null);
    const [chatterUser, setChatterUser] = useState(null);
    const [inviteCode, setInviteCode] = useState('');
    const [creatorUsername, setCreatorUsername] = useState('');
    const [profileName, setProfileName] = useState('');
    const [profileTimezone, setProfileTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || '');
    const [profileLanguages, setProfileLanguages] = useState([{ lang: '', level: 'Fluent' }]);
    const [profileBio, setProfileBio] = useState('');
    const [email, setEmail] = useState('');
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [profilePicPreview, setProfilePicPreview] = useState(null);
    const [pricingType, setPricingType] = useState('negotiable');
    const [proposedHourlyRate, setProposedHourlyRate] = useState('');
    const [proposedPercentage, setProposedPercentage] = useState('');
    const [copied, setCopied] = useState(false);
    // Creator onboarding step 6 state
    const [creatorChannelAdded, setCreatorChannelAdded] = useState(false);
    const [addingChannelOnboarding, setAddingChannelOnboarding] = useState(false);

    const passwordErrors = !isLogin && registrationStep === 2 ? validatePassword(password) : [];
    const passwordsMatch = password === confirmPassword;

    // Google Sign-In handler - MUST be defined before the useEffect that references it
    const handleGoogleSignIn = useCallback(async (response) => {
        if (!response.credential) { setError('No credential received from Google'); return; }
        setLoading(true); setError('');
        try {
            const res = await api.post('/api/dashboard/google-auth', { credential: response.credential, is_login: isLogin });
            login(res.data.token, { username: res.data.username });
            navigate('/dashboard');
        } catch (err) { setError(err.response?.data?.error || 'Google sign-in failed'); }
        finally { setLoading(false); }
    }, [isLogin, login, navigate]);

    // Creator connect telegram polling (step 5)
    useEffect(() => {
        if (isLogin || registrationStep !== 5) return;
        let cancelled = false;
        const poll = async () => {
            try {
                const res = await api.post('/api/dashboard/account-info', { token: localStorage.getItem('token') });
                if (cancelled) return;
                if (res.data.telegram_linked) {
                    setCreatorTgConnected(true);
                    setTimeout(() => { sessionStorage.setItem('creatorOnboarding', '6'); setRegistrationStep(6); }, 1500);
                } else if (res.data.connect_code) {
                    setCreatorConnectCode(res.data.connect_code);
                }
            } catch {}
        };
        poll();
        const i = setInterval(poll, 2500);
        return () => { cancelled = true; clearInterval(i); };
    }, [isLogin, registrationStep]);

    // Creator add channel polling (step 6)
    useEffect(() => {
        if (isLogin || registrationStep !== 6 || !addingChannelOnboarding) return;
        let cancelled = false;
        const poll = async () => {
            try {
                const res = await api.get('/api/wheels');
                if (cancelled) return;
                if (res.data.wheels && res.data.wheels.length > 0) {
                    setCreatorChannelAdded(true);
                    setTimeout(() => { sessionStorage.removeItem('creatorOnboarding'); navigate('/dashboard'); }, 1500);
                }
            } catch {}
        };
        poll();
        const i = setInterval(poll, 3000);
        return () => { cancelled = true; clearInterval(i); };
    }, [isLogin, registrationStep, addingChannelOnboarding, navigate]);

    // Initialize Google Sign-In
    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return;
        const initGoogle = () => {
            if (window.google?.accounts) {
                window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleGoogleSignIn, auto_select: false });
            }
        };
        initGoogle();
        const interval = setInterval(initGoogle, 500);
        const timeout = setTimeout(() => clearInterval(interval), 3000);
        return () => { clearInterval(interval); clearTimeout(timeout); };
    }, [handleGoogleSignIn]);

    const triggerGoogleSignIn = () => {
        if (!window.google?.accounts) { setError('Google Sign-In not loaded. Please refresh.'); return; }
        window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed()) {
                setError(notification.getNotDisplayedReason() === 'opt_out_or_no_session'
                    ? 'Please sign in to Google in your browser first.'
                    : 'Google Sign-In unavailable. Use username/password.');
            }
        });
    };

    const handleNextStep = async (e) => {
        e.preventDefault(); setError('');
        if (!username || username.length < 3) { setError('Username must be at least 3 characters'); return; }
        if (username.length > 30) { setError('Username must be 30 characters or less'); return; }
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address'); return; }
        setLoading(true);
        try {
            const res = await api.post('/api/dashboard/check-username', { username });
            if (res.data.taken) { setError('Username is already taken'); setLoading(false); return; }
        } catch (err) { /* continue - backend will check on registration */ }
        setLoading(false);
        setRegistrationStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); setError('');
        setLoading(true);
        try {
            const res = await api.post('/api/dashboard/login', { username, password });
            const isSuperadmin = res.data.is_superadmin === true;
            login(res.data.token, { username: res.data.username }, isSuperadmin);
            navigate('/dashboard');
        } catch (dashErr) {
            try {
                const res = await api.post('/api/chatter/login', { username, password });
                if (res.data.user?.onboarding_status === 'pending') {
                    setChatterToken(res.data.token);
                    setChatterUser(res.data.user);
                    setIsLogin(false);
                    setRegistrationStep(4);
                    setOnboardingStep('choose');
                } else {
                    login(res.data.token, res.data.user, false, 'chatter');
                    navigate('/dashboard');
                }
            } catch (chatErr) {
                setError('Invalid username or password');
            }
        } finally { setLoading(false); }
    };

    const handlePasswordStep = (e) => {
        e.preventDefault(); setError('');
        if (passwordErrors.length > 0) { setError('Password requirements not met'); return; }
        if (!passwordsMatch) { setError('Passwords do not match'); return; }
        setRegistrationStep(3);
    };

    const handleRegister = async (type) => {
        setLoading(true); setError('');
        try {
            if (type === 'creator') {
                const res = await api.post('/api/dashboard/register', { username, password, email });
                const isSuperadmin = res.data.is_superadmin === true;
                login(res.data.token, { username: res.data.username }, isSuperadmin);
                if (isSuperadmin) { navigate('/dashboard'); return; }
                // Show telegram connect onboarding step
                sessionStorage.setItem('creatorOnboarding', '5');
                setRegistrationStep(5);
            } else {
                const res = await api.post('/api/chatter/register', { username, password, email, display_name: username });
                // Store token for onboarding API calls, don't navigate yet
                setChatterToken(res.data.token);
                setChatterUser(res.data.user);
                setRegistrationStep(4);
                setOnboardingStep('choose');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed');
        } finally { setLoading(false); }
    };

    const handleRedeemCode = async (e) => {
        e.preventDefault(); setError(''); setLoading(true);
        try {
            await api.post('/api/chatter/redeem-code', { code: inviteCode }, {
                headers: { Authorization: `Bearer ${chatterToken}` }
            });
            await api.post('/api/chatter/complete-onboarding', {}, {
                headers: { Authorization: `Bearer ${chatterToken}` }
            });
            login(chatterToken, { ...chatterUser, onboarding_status: 'completed' }, false, 'chatter');
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid code');
        } finally { setLoading(false); }
    };

    const handleSendRequest = async (e) => {
        e.preventDefault(); setError(''); setLoading(true);
        try {
            await api.post('/api/chatter/connect-request', { creator_username: creatorUsername }, {
                headers: { Authorization: `Bearer ${chatterToken}` }
            });
            setChatterUser(prev => ({ ...prev, onboarding_status: 'completed' }));
            setOnboardingStep('requestSent');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to send request');
        } finally { setLoading(false); }
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault(); setError(''); setLoading(true);
        try {
            // Upload profile picture if selected
            if (profilePicFile) {
                const formData = new FormData();
                formData.append('image', profilePicFile);
                formData.append('token', chatterToken);
                await api.post('/api/profile/upload-picture', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
            await api.post('/api/chatter/save-profile', {
                display_name: profileName,
                timezone: profileTimezone,
                languages: profileLanguages.filter(l => l.lang.trim()),
                bio: profileBio,
                pricing_type: pricingType,
                proposed_hourly_rate: proposedHourlyRate || null,
                proposed_percentage: proposedPercentage || null,
            }, { headers: { Authorization: `Bearer ${chatterToken}` } });
            login(chatterToken, { ...chatterUser, onboarding_status: 'completed' }, false, 'chatter');
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save profile');
        } finally { setLoading(false); }
    };

    const goToDashboard = () => {
        login(chatterToken, { ...chatterUser, onboarding_status: 'completed' }, false, 'chatter');
        navigate('/dashboard');
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault(); setError(''); setSuccess(''); setLoading(true);
        try {
            await api.post('/api/dashboard/reset-password-request', { username: forgotUsername });
            setSuccess('Reset code sent to your Telegram! Check your DMs with @' + BOT_USERNAME);
            setShowResetForm(true);
        } catch (err) { setError(err.response?.data?.error || 'Failed to send reset code'); }
        finally { setLoading(false); }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault(); setError(''); setSuccess(''); setLoading(true);
        if (resetNewPassword !== resetConfirmPassword) { setError('Passwords do not match'); setLoading(false); return; }
        try {
            await api.post('/api/dashboard/reset-password', { token: resetCode, new_password: resetNewPassword });
            setSuccess('Password reset successful! You can now login.');
            setTimeout(() => { setShowForgot(false); setShowResetForm(false); setResetCode(''); setResetNewPassword(''); setResetConfirmPassword(''); }, 2000);
        } catch (err) { setError(err.response?.data?.error || 'Failed to reset password'); }
        finally { setLoading(false); }
    };

    const usernameHandler = (e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''));

    // ---- RENDER: Reset password code entry ----
    if (showForgot && showResetForm) {
        return (
            <div style={pageWrap}>

                <div style={cardSt}>
                    <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '10px', color: 'var(--text)' }}>Reset Password</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '30px' }}>Check your Telegram for the code</p>
                    {error && <div style={errorBox}>{error}</div>}
                    {success && <div style={successBox}>{success}</div>}
                    <form onSubmit={handleResetPassword}>
                        <div style={inputBox}><input type="text" value={resetCode} onChange={e => setResetCode(e.target.value)} placeholder="Reset code" required style={inputStyle} /><span style={inputIcon}><Icons.Key /></span></div>
                        <div style={inputBox}><input type="password" value={resetNewPassword} onChange={e => setResetNewPassword(e.target.value)} placeholder="New password" required minLength={6} style={inputStyle} /><span style={inputIcon}><Icons.Lock /></span></div>
                        <div style={inputBox}><input type="password" value={resetConfirmPassword} onChange={e => setResetConfirmPassword(e.target.value)} placeholder="Confirm password" required minLength={6} style={inputStyle} /><span style={inputIcon}><Icons.Lock /></span></div>
                        <button type="submit" disabled={loading} style={{ ...btnPrimary, marginBottom: '20px', opacity: loading ? 0.7 : 1 }}>{loading ? 'Resetting...' : 'Reset Password'}</button>
                    </form>
                    <button onClick={() => { setShowResetForm(false); setError(''); setSuccess(''); }} style={btnGhost}>← Back</button>
                </div>
            </div>
        );
    }

    // ---- RENDER: Forgot password username entry ----
    if (showForgot) {
        return (
            <div style={pageWrap}>

                <div style={cardSt}>
                    <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '10px', color: 'var(--text)' }}>Reset Password</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '30px' }}>Enter your username to receive a reset code</p>
                    {error && <div style={errorBox}>{error}</div>}
                    {success && <div style={successBox}>{success}</div>}
                    <form onSubmit={handleForgotPassword}>
                        <div style={inputBox}><input type="text" value={forgotUsername} onChange={e => setForgotUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))} placeholder="Your username" required style={inputStyle} /><span style={inputIcon}><Icons.User /></span></div>
                        <button type="submit" disabled={loading} style={{ ...btnPrimary, marginBottom: '20px', opacity: loading ? 0.7 : 1 }}>{loading ? 'Sending...' : 'Send Reset Code'}</button>
                    </form>
                    <button onClick={() => { setShowForgot(false); setError(''); setSuccess(''); }} style={btnGhost}>← Back to Login</button>
                </div>
            </div>
        );
    }

    // ---- RENDER: Sign Up Step 1 (Landing-themed) ----
    if (!isLogin && registrationStep === 1) {
        return (
            <div style={signupPageWrap}>
                <div style={signupCardSt}>
                    <h2 style={{ fontSize: '32px', fontWeight: 800, marginBottom: '6px', color: L.text, letterSpacing: '-0.5px' }}>Create your account</h2>
                    <p style={{ color: L.textMuted, fontSize: '15px', marginBottom: '28px', lineHeight: 1.5 }}>Step 1 of 3 — Your details</p>
                    {error && <div style={signupErrorBox}>{error}</div>}
                    <form onSubmit={handleNextStep}>
                        <div style={inputBox}>
                            <input type="text" value={username} onChange={usernameHandler} placeholder="Username" required minLength={3} maxLength={30}
                                style={signupInputSt} autoFocus
                                onFocus={e => e.target.style.borderColor = L.blue}
                                onBlur={e => e.target.style.borderColor = L.border} />
                            <span style={signupInputIcon}><Icons.User /></span>
                        </div>
                        <p style={{ fontSize: '12px', color: L.textMuted, marginTop: '-12px', marginBottom: '20px' }}>3-30 characters, letters and numbers only</p>
                        <div style={inputBox}>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" required
                                style={signupInputSt}
                                onFocus={e => e.target.style.borderColor = L.blue}
                                onBlur={e => e.target.style.borderColor = L.border} />
                            <span style={signupInputIcon}><Icons.Mail /></span>
                        </div>
                        <button type="submit" disabled={loading}
                            style={{ ...signupBtn, marginBottom: '20px', opacity: loading ? 0.7 : 1 }}
                            onMouseEnter={e => { e.currentTarget.style.background = L.blueHover; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,174,239,0.3)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = L.blue; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,174,239,0.25)'; }}>
                            {loading ? 'Checking...' : 'Continue'}
                        </button>
                    </form>
                    <GoogleSignInButton onClick={triggerGoogleSignIn} loading={loading} landing />
                    <div style={{ textAlign: 'center', color: L.textSecondary, fontSize: '14px' }}>
                        Already have an account?{' '}
                        <span onClick={() => { setIsLogin(true); setRegistrationStep(1); setError(''); }}
                            style={{ fontWeight: 600, cursor: 'pointer', color: L.blue }}>Login</span>
                    </div>
                </div>
            </div>
        );
    }

    // ---- RENDER: Sign Up Step 2 (Landing-themed) ----
    if (!isLogin && registrationStep === 2) {
        return (
            <div style={signupPageWrap}>
                <div style={signupCardSt}>
                    <h2 style={{ fontSize: '32px', fontWeight: 800, marginBottom: '6px', color: L.text, letterSpacing: '-0.5px' }}>Set your password</h2>
                    <p style={{ color: L.textMuted, fontSize: '15px', marginBottom: '28px', lineHeight: 1.5 }}>Step 2 of 3 — Secure your account</p>
                    <div style={{ background: L.bgAlt, borderRadius: '12px', padding: '12px 16px', marginBottom: '20px', fontSize: '14px', color: L.textSecondary, border: `1px solid ${L.border}` }}>
                        Username: <strong style={{ color: L.text }}>@{username}</strong>
                    </div>
                    {error && <div style={signupErrorBox}>{error}</div>}
                    <form onSubmit={handlePasswordStep}>
                        <div style={inputBox}>
                            <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required
                                style={signupInputSt} autoComplete="new-password" autoFocus
                                onFocus={e => e.target.style.borderColor = L.blue}
                                onBlur={e => e.target.style.borderColor = L.border} />
                            <span style={{ ...signupInputIcon, cursor: 'pointer' }} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <Icons.Eye /> : <Icons.EyeOff />}</span>
                        </div>
                        <div style={{ marginTop: '-10px', marginBottom: '16px', padding: '12px', background: L.bgAlt, borderRadius: '10px', border: `1px solid ${L.border}` }}>
                            <div style={{ fontSize: '11px', color: L.textMuted, marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>Password Requirements</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '12px' }}>
                                {PASSWORD_RULES.map((req, i) => {
                                    const ok = req.test(password);
                                    return (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: ok ? L.success : L.textMuted }}>
                                            {ok ? <Icons.Check size={14} /> : <Icons.X size={14} />}
                                            <span>{req.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div style={inputBox}>
                            <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm Password" required
                                style={{ ...signupInputSt, borderColor: confirmPassword && !passwordsMatch ? L.error : confirmPassword && passwordsMatch ? L.success : L.border }} autoComplete="new-password" />
                            <span style={{ ...signupInputIcon, cursor: 'pointer' }} onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? <Icons.Eye /> : <Icons.EyeOff />}</span>
                        </div>
                        {confirmPassword && !passwordsMatch && <p style={{ fontSize: '12px', color: L.error, marginTop: '-12px', marginBottom: '12px' }}>Passwords do not match</p>}
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button type="button" onClick={() => setRegistrationStep(1)}
                                style={{ ...signupBtnSecondary, flex: '0 0 auto' }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.color = L.blue; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.color = L.text; }}>
                                ← Back
                            </button>
                            <button type="submit" disabled={loading || passwordErrors.length > 0 || !passwordsMatch || !confirmPassword}
                                style={{ ...signupBtn, flex: 1, opacity: (passwordErrors.length > 0 || !passwordsMatch || !confirmPassword) ? 0.5 : 1 }}
                                onMouseEnter={e => { if (passwordErrors.length === 0 && passwordsMatch && confirmPassword) { e.currentTarget.style.background = L.blueHover; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                                onMouseLeave={e => { e.currentTarget.style.background = L.blue; e.currentTarget.style.transform = 'translateY(0)'; }}>
                                Continue
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    // ---- RENDER: Sign Up Step 3 — Account Type ----
    if (!isLogin && registrationStep === 3) {
        const roleCard = (type, icon, title, desc, delay) => (
            <button onClick={() => handleRegister(type)} disabled={loading}
                style={{
                    flex: 1, minWidth: '160px', padding: '32px 20px', background: '#fff',
                    border: `2px solid ${L.border}`, borderRadius: '20px', cursor: 'pointer',
                    textAlign: 'center', transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
                    fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
                    animation: `fadeSlideUp 0.5s ${delay}s both cubic-bezier(.4,0,.2,1)`,
                    position: 'relative', overflow: 'hidden',
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.borderColor = L.blue;
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,174,239,0.15)';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.borderColor = L.border;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}>
                <div style={{ fontSize: '40px', marginBottom: '14px', lineHeight: 1 }}>{icon}</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: L.text, marginBottom: '8px' }}>{title}</div>
                <div style={{ fontSize: '13px', color: L.textMuted, lineHeight: 1.5 }}>{desc}</div>
            </button>
        );

        return (
            <div style={signupPageWrap}>
                <style>{`
                    @keyframes fadeSlideUp {
                        from { opacity: 0; transform: translateY(24px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                `}</style>
                <div style={{ textAlign: 'center', maxWidth: '480px', width: '100%' }}>
                    <h2 style={{
                        fontSize: '28px', fontWeight: 800, marginBottom: '8px', color: L.text,
                        letterSpacing: '-0.5px', animation: 'fadeIn 0.4s both',
                    }}>Which one describes you better?</h2>
                    <p style={{
                        color: L.textMuted, fontSize: '15px', marginBottom: '32px', lineHeight: 1.5,
                        animation: 'fadeIn 0.4s 0.1s both',
                    }}>Step 3 of 3 — Choose your role</p>
                    {error && <div style={{ ...signupErrorBox, animation: 'fadeIn 0.3s both' }}>{error}</div>}
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '28px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {roleCard('creator',
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={L.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>,
                            'Creator',
                            'Build & monetize your Telegram channels',
                            0.15
                        )}
                        {roleCard('chatter',
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={L.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
                            'Chatter',
                            'Manage chats & content for creators',
                            0.3
                        )}
                    </div>
                    {loading && <p style={{ fontSize: '14px', color: L.textMuted, marginBottom: '16px', animation: 'fadeIn 0.3s both' }}>Creating your account...</p>}
                    <button type="button" onClick={() => setRegistrationStep(2)} disabled={loading}
                        style={{ ...signupBtnSecondary, animation: 'fadeIn 0.5s 0.4s both' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.color = L.blue; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.color = L.text; }}>
                        &#8592; Back
                    </button>
                </div>
            </div>
        );
    }

    // ---- RENDER: Onboarding Step 4a — Choose your path ----
    if (!isLogin && registrationStep === 4 && onboardingStep === 'choose') {
        const pathCard = (path, icon, title, desc, delay) => (
            <button onClick={() => { setError(''); setOnboardingStep(path); }}
                style={{
                    flex: 1, minWidth: '160px', padding: '32px 20px', background: '#fff',
                    border: `2px solid ${L.border}`, borderRadius: '20px', cursor: 'pointer',
                    textAlign: 'center', transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
                    fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
                    animation: `fadeSlideUp 0.5s ${delay}s both cubic-bezier(.4,0,.2,1)`,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,174,239,0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ fontSize: '40px', marginBottom: '14px', lineHeight: 1 }}>{icon}</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: L.text, marginBottom: '8px' }}>{title}</div>
                <div style={{ fontSize: '13px', color: L.textMuted, lineHeight: 1.5 }}>{desc}</div>
            </button>
        );

        return (
            <div style={signupPageWrap}>
                <style>{`
                    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                `}</style>
                <div style={{ textAlign: 'center', maxWidth: '480px', width: '100%' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '8px', color: L.text, letterSpacing: '-0.5px', animation: 'fadeIn 0.4s both' }}>
                        What's your situation?
                    </h2>
                    <p style={{ color: L.textMuted, fontSize: '15px', marginBottom: '32px', lineHeight: 1.5, animation: 'fadeIn 0.4s 0.1s both' }}>
                        Let us help you get started
                    </p>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '28px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {pathCard('connect',
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={L.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>,
                            'Ready to connect',
                            'I have a creator to work with',
                            0.15
                        )}
                        {pathCard('profile',
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={L.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>,
                            'Looking for a job',
                            'I want creators to find me',
                            0.3
                        )}
                    </div>
                    <button type="button" onClick={async () => {
                        try { await api.post('/api/chatter/delete-account', {}, { headers: { Authorization: `Bearer ${chatterToken}` } }); } catch {}
                        setChatterToken(null); setChatterUser(null); setRegistrationStep(3); setOnboardingStep(null);
                    }}
                        style={{ ...signupBtnSecondary, animation: 'fadeIn 0.5s 0.4s both' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.color = L.blue; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.color = L.text; }}>
                        &#8592; Back
                    </button>
                </div>
            </div>
        );
    }

    // ---- RENDER: Onboarding — Connect options ----
    if (!isLogin && registrationStep === 4 && onboardingStep === 'connect') {
        const optCard = (step, icon, title, desc, delay) => (
            <button onClick={() => { setError(''); setOnboardingStep(step); }}
                style={{
                    flex: 1, minWidth: '160px', padding: '32px 20px', background: '#fff',
                    border: `2px solid ${L.border}`, borderRadius: '20px', cursor: 'pointer',
                    textAlign: 'center', transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
                    fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
                    animation: `fadeSlideUp 0.5s ${delay}s both cubic-bezier(.4,0,.2,1)`,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,174,239,0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ fontSize: '40px', marginBottom: '14px', lineHeight: 1 }}>{icon}</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: L.text, marginBottom: '8px' }}>{title}</div>
                <div style={{ fontSize: '13px', color: L.textMuted, lineHeight: 1.5 }}>{desc}</div>
            </button>
        );

        return (
            <div style={signupPageWrap}>
                <style>{`
                    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                `}</style>
                <div style={{ textAlign: 'center', maxWidth: '480px', width: '100%' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '8px', color: L.text, letterSpacing: '-0.5px', animation: 'fadeIn 0.4s both' }}>
                        How do you want to connect?
                    </h2>
                    <p style={{ color: L.textMuted, fontSize: '15px', marginBottom: '32px', lineHeight: 1.5, animation: 'fadeIn 0.4s 0.1s both' }}>
                        Choose how to join your creator
                    </p>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '28px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {optCard('codeEntry',
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={L.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>,
                            'I have an invite code',
                            'Enter the code from your creator',
                            0.15
                        )}
                        {optCard('requestCreator',
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={L.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/></svg>,
                            'Send a request',
                            'Request to join a specific creator',
                            0.3
                        )}
                    </div>
                    <button type="button" onClick={() => setOnboardingStep('choose')}
                        style={{ ...signupBtnSecondary, animation: 'fadeIn 0.5s 0.4s both' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.color = L.blue; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.color = L.text; }}>
                        &#8592; Back
                    </button>
                </div>
            </div>
        );
    }

    // ---- RENDER: Onboarding — Invite Code Entry ----
    if (!isLogin && registrationStep === 4 && onboardingStep === 'codeEntry') {
        return (
            <div style={signupPageWrap}>
                <style>{`
                    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                `}</style>
                <div style={{ ...signupCardSt, animation: 'fadeSlideUp 0.5s both cubic-bezier(.4,0,.2,1)' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '8px', color: L.text, letterSpacing: '-0.5px' }}>Enter invite code</h2>
                    <p style={{ color: L.textMuted, fontSize: '15px', marginBottom: '28px', lineHeight: 1.5 }}>
                        Paste the code your creator gave you
                    </p>
                    {error && <div style={signupErrorBox}>{error}</div>}
                    <form onSubmit={handleRedeemCode}>
                        <div style={inputBox}>
                            <input type="text" value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())}
                                placeholder="INVITE CODE" required style={{ ...signupInputSt, letterSpacing: '2px', fontWeight: 600, textAlign: 'center' }}
                                autoFocus onFocus={e => e.target.style.borderColor = L.blue} onBlur={e => e.target.style.borderColor = L.border} />
                        </div>
                        <button type="submit" disabled={loading || !inviteCode.trim()}
                            style={{ ...signupBtn, marginBottom: '20px', opacity: loading || !inviteCode.trim() ? 0.5 : 1 }}
                            onMouseEnter={e => { if (inviteCode.trim()) { e.currentTarget.style.background = L.blueHover; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                            onMouseLeave={e => { e.currentTarget.style.background = L.blue; e.currentTarget.style.transform = 'translateY(0)'; }}>
                            {loading ? 'Redeeming...' : 'Redeem Code'}
                        </button>
                    </form>
                    <button type="button" onClick={() => setOnboardingStep('connect')}
                        style={signupBtnSecondary}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.color = L.blue; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.color = L.text; }}>
                        &#8592; Back
                    </button>
                </div>
            </div>
        );
    }

    // ---- RENDER: Onboarding — Send Request to Creator ----
    if (!isLogin && registrationStep === 4 && onboardingStep === 'requestCreator') {
        return (
            <div style={signupPageWrap}>
                <style>{`
                    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                `}</style>
                <div style={{ ...signupCardSt, animation: 'fadeSlideUp 0.5s both cubic-bezier(.4,0,.2,1)' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '8px', color: L.text, letterSpacing: '-0.5px' }}>Send a request</h2>
                    <p style={{ color: L.textMuted, fontSize: '15px', marginBottom: '28px', lineHeight: 1.5 }}>
                        Enter the creator's username to request access
                    </p>
                    {error && <div style={signupErrorBox}>{error}</div>}
                    <form onSubmit={handleSendRequest}>
                        <div style={inputBox}>
                            <input type="text" value={creatorUsername}
                                onChange={e => setCreatorUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                                placeholder="Creator username" required style={signupInputSt}
                                autoFocus onFocus={e => e.target.style.borderColor = L.blue} onBlur={e => e.target.style.borderColor = L.border} />
                            <span style={{ ...signupInputIcon, pointerEvents: 'none', color: L.textMuted, fontWeight: 600, fontSize: '16px' }}>@</span>
                        </div>
                        <button type="submit" disabled={loading || !creatorUsername.trim()}
                            style={{ ...signupBtn, marginBottom: '20px', opacity: loading || !creatorUsername.trim() ? 0.5 : 1 }}
                            onMouseEnter={e => { if (creatorUsername.trim()) { e.currentTarget.style.background = L.blueHover; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                            onMouseLeave={e => { e.currentTarget.style.background = L.blue; e.currentTarget.style.transform = 'translateY(0)'; }}>
                            {loading ? 'Sending...' : 'Send Request'}
                        </button>
                    </form>
                    <button type="button" onClick={() => setOnboardingStep('connect')}
                        style={signupBtnSecondary}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.color = L.blue; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.color = L.text; }}>
                        &#8592; Back
                    </button>
                </div>
            </div>
        );
    }

    // ---- RENDER: Onboarding — Request Sent confirmation ----
    if (!isLogin && registrationStep === 4 && onboardingStep === 'requestSent') {
        return (
            <div style={signupPageWrap}>
                <style>{`
                    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                `}</style>
                <div style={{ ...signupCardSt, animation: 'fadeSlideUp 0.5s both cubic-bezier(.4,0,.2,1)' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'fadeIn 0.4s 0.2s both' }}>&#9989;</div>
                    <h2 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '8px', color: L.text, letterSpacing: '-0.5px' }}>Request sent!</h2>
                    <p style={{ color: L.textMuted, fontSize: '15px', marginBottom: '8px', lineHeight: 1.6 }}>
                        Your request to <strong style={{ color: L.text }}>@{creatorUsername}</strong> has been sent.
                    </p>
                    <p style={{ color: L.textMuted, fontSize: '14px', marginBottom: '28px', lineHeight: 1.6 }}>
                        You'll get a notification when they respond. You can check your dashboard in the meantime.
                    </p>
                    <button type="button" onClick={goToDashboard}
                        style={signupBtn}
                        onMouseEnter={e => { e.currentTarget.style.background = L.blueHover; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = L.blue; e.currentTarget.style.transform = 'translateY(0)'; }}>
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    // ---- RENDER: Onboarding — Profile Builder ----
    if (!isLogin && registrationStep === 4 && onboardingStep === 'profile') {
        const timezones = (() => { try { return Intl.supportedValuesOf('timeZone'); } catch { return []; } })();
        const languages = [
            'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Russian',
            'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi', 'Turkish', 'Dutch', 'Polish',
            'Swedish', 'Norwegian', 'Danish', 'Finnish', 'Czech', 'Romanian', 'Hungarian',
            'Greek', 'Thai', 'Vietnamese', 'Indonesian', 'Malay', 'Filipino', 'Ukrainian',
            'Hebrew', 'Persian', 'Bengali', 'Urdu', 'Swahili',
        ];
        const usedLangs = profileLanguages.map(l => l.lang);
        const addLanguage = () => setProfileLanguages(prev => [...prev, { lang: '', level: 'Fluent' }]);
        const removeLanguage = (i) => setProfileLanguages(prev => prev.filter((_, idx) => idx !== i));
        const updateLanguage = (i, field, val) => setProfileLanguages(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l));


        return (
            <div style={signupPageWrap}>
                <style>{`
                    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                `}</style>
                <div style={{ ...signupCardSt, maxWidth: '480px', animation: 'fadeSlideUp 0.5s both cubic-bezier(.4,0,.2,1)' }}>
                    <h2 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '8px', color: L.text, letterSpacing: '-0.5px' }}>Build your profile</h2>
                    <p style={{ color: L.textMuted, fontSize: '15px', marginBottom: '28px', lineHeight: 1.5 }}>
                        Help creators find you
                    </p>
                    {error && <div style={signupErrorBox}>{error}</div>}
                    <form onSubmit={handleSaveProfile}>
                        {/* Profile Picture */}
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <div style={{ width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', margin: '0 auto 10px', background: `linear-gradient(135deg, ${L.blue}, #0088cc)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '28px', fontWeight: 700 }}>
                                {profilePicPreview ? (
                                    <img src={profilePicPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    (profileName || username || 'C')[0].toUpperCase()
                                )}
                            </div>
                            <label htmlFor="profile-pic-upload" style={{ color: L.blue, cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                                Upload photo
                            </label>
                            <input type="file" id="profile-pic-upload" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                                onChange={e => { const f = e.target.files[0]; if (f) { setProfilePicFile(f); setProfilePicPreview(URL.createObjectURL(f)); } }} />
                        </div>

                        {/* Nickname */}
                        <div style={{ textAlign: 'left', marginBottom: '6px' }}>
                            <label style={{ fontSize: '13px', fontWeight: 600, color: L.textSecondary }}>Nickname</label>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <input type="text" value={profileName} onChange={e => setProfileName(e.target.value)}
                                placeholder="Your nickname" required style={{ ...signupInputSt, height: '48px' }}
                                autoFocus onFocus={e => e.target.style.borderColor = L.blue} onBlur={e => e.target.style.borderColor = L.border} />
                        </div>

                        {/* Timezone */}
                        <div style={{ textAlign: 'left', marginBottom: '6px' }}>
                            <label style={{ fontSize: '13px', fontWeight: 600, color: L.textSecondary }}>Timezone</label>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <CustomSelect value={profileTimezone} onChange={v => setProfileTimezone(v)}
                                options={timezones.map(tz => { try { const f = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' }); const p = f.formatToParts(new Date()); const off = p.find(x => x.type === 'timeZoneName')?.value || ''; return { value: tz, label: `${tz.replace(/_/g, ' ')} (${off})` }; } catch { return { value: tz, label: tz.replace(/_/g, ' ') }; } })}
                                placeholder="Select timezone" searchable />
                        </div>

                        {/* Languages */}
                        <div style={{ textAlign: 'left', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={{ fontSize: '13px', fontWeight: 600, color: L.textSecondary }}>Languages</label>
                            <button type="button" onClick={addLanguage}
                                style={{ fontSize: '13px', color: L.blue, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>
                                + Add language
                            </button>
                        </div>
                        {profileLanguages.map((lang, i) => (
                            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                                <CustomSelect value={lang.lang} onChange={v => updateLanguage(i, 'lang', v)}
                                    options={languages.filter(l => l === lang.lang || !usedLangs.includes(l)).map(l => ({ value: l, label: l }))}
                                    placeholder="Select language" searchable style={{ flex: 1 }} />
                                <CustomSelect value={lang.level} onChange={v => updateLanguage(i, 'level', v)}
                                    options={[{ value: 'Native', label: 'Native' }, { value: 'Fluent', label: 'Fluent' }, { value: 'Basic', label: 'Basic' }]}
                                    style={{ width: '120px', flex: 'none' }} />
                                {profileLanguages.length > 1 && (
                                    <button type="button" onClick={() => removeLanguage(i)}
                                        style={{ background: 'none', border: 'none', color: L.error, cursor: 'pointer', fontSize: '18px', padding: '4px', lineHeight: 1, flexShrink: 0 }}>
                                        &times;
                                    </button>
                                )}
                            </div>
                        ))}

                        {/* Bio */}
                        <div style={{ textAlign: 'left', marginBottom: '6px', marginTop: '6px' }}>
                            <label style={{ fontSize: '13px', fontWeight: 600, color: L.textSecondary }}>Bio</label>
                        </div>
                        <div style={{ marginBottom: '16px', position: 'relative' }}>
                            <textarea value={profileBio} onChange={e => setProfileBio(e.target.value.slice(0, 500))}
                                placeholder="Tell creators about yourself..." maxLength={500} rows={4}
                                style={{ ...signupInputSt, resize: 'vertical', minHeight: '100px', padding: '14px 18px' }}
                                onFocus={e => e.target.style.borderColor = L.blue} onBlur={e => e.target.style.borderColor = L.border} />
                            <div style={{ textAlign: 'right', fontSize: '12px', color: L.textMuted, marginTop: '4px' }}>{profileBio.length}/500</div>
                        </div>

                        {/* Pricing */}
                        <div style={{ textAlign: 'left', marginBottom: '6px' }}>
                            <label style={{ fontSize: '13px', fontWeight: 600, color: L.textSecondary }}>Your Rates</label>
                            <p style={{ fontSize: '11px', color: L.textMuted, margin: '4px 0 10px' }}>Set your proposed rates — you'll finalize through negotiation with creators.</p>
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <CustomSelect value={pricingType} onChange={v => setPricingType(v)}
                                options={[
                                    { value: 'negotiable', label: 'Open to negotiate' },
                                    { value: 'hourly', label: 'Hourly rate' },
                                    { value: 'percentage', label: '% of my sales' },
                                    { value: 'both', label: 'Hourly + % of sales' },
                                ]} />
                        </div>
                        {(pricingType === 'hourly' || pricingType === 'percentage' || pricingType === 'both') && (
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                                {(pricingType === 'hourly' || pricingType === 'both') && (
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <input type="number" value={proposedHourlyRate} onChange={e => setProposedHourlyRate(e.target.value)}
                                            placeholder="Hourly rate" min="0" max="100" step="0.50" style={{ ...signupInputSt, height: '48px', paddingRight: '36px' }}
                                            onFocus={e => e.target.style.borderColor = L.blue} onBlur={e => e.target.style.borderColor = L.border} />
                                        <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', fontWeight: 600, color: L.textMuted, pointerEvents: 'none' }}>$</span>
                                    </div>
                                )}
                                {(pricingType === 'percentage' || pricingType === 'both') && (
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <input type="number" value={proposedPercentage} onChange={e => setProposedPercentage(e.target.value)}
                                            placeholder="Sales cut" min="1" max="100" step="1" style={{ ...signupInputSt, height: '48px', paddingRight: '36px' }}
                                            onFocus={e => e.target.style.borderColor = L.blue} onBlur={e => e.target.style.borderColor = L.border} />
                                        <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', fontWeight: 600, color: L.textMuted, pointerEvents: 'none' }}>%</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <button type="submit" disabled={loading || !profileName.trim()}
                            style={{ ...signupBtn, marginBottom: '20px', opacity: loading || !profileName.trim() ? 0.5 : 1 }}
                            onMouseEnter={e => { if (profileName.trim()) { e.currentTarget.style.background = L.blueHover; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                            onMouseLeave={e => { e.currentTarget.style.background = L.blue; e.currentTarget.style.transform = 'translateY(0)'; }}>
                            {loading ? 'Saving...' : 'Save & Continue'}
                        </button>
                    </form>
                    <button type="button" onClick={() => setOnboardingStep('choose')}
                        style={signupBtnSecondary}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.color = L.blue; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.color = L.text; }}>
                        &#8592; Back
                    </button>
                </div>
            </div>
        );
    }

    // ---- RENDER: Creator Onboarding — Connect Telegram ----
    if (!isLogin && registrationStep === 5) {
        const deepLink = `https://t.me/${BOT_USERNAME}?start=connect_${creatorConnectCode}`;
        const copyCode = () => { navigator.clipboard.writeText(`/connect ${creatorConnectCode}`); setCopied(true); setTimeout(() => setCopied(false), 2000); };

        return (
            <div style={signupPageWrap}>
                <style>{`
                    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes pulse2 { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                `}</style>
                <div style={{ ...signupCardSt, maxWidth: '460px', animation: 'fadeSlideUp 0.5s both cubic-bezier(.4,0,.2,1)' }}>

                    {creatorTgConnected ? (
                        <div style={{ animation: 'fadeSlideUp 0.4s both' }}>
                            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            </div>
                            <h2 style={{ fontSize: '24px', fontWeight: 800, color: L.text, marginBottom: '8px' }}>Connected!</h2>
                            <p style={{ color: L.textMuted, fontSize: '14px' }}>Redirecting to your dashboard...</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ animation: 'fadeIn 0.4s both' }}>
                                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(0,174,239,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={L.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>
                                </div>
                                <h2 style={{ fontSize: '24px', fontWeight: 800, color: L.text, marginBottom: '4px' }}>Connect Telegram</h2>
                                <p style={{ color: L.textMuted, fontSize: '14px', marginBottom: '24px', lineHeight: 1.5 }}>
                                    Link your Telegram to manage channels and receive notifications
                                </p>
                            </div>

                            {creatorConnectCode && (
                                <div style={{ animation: 'fadeSlideUp 0.5s 0.1s both' }}>
                                    {/* Code display */}
                                    <div style={{ background: '#f8fafc', border: `2px solid ${L.border}`, borderRadius: '14px', padding: '18px', marginBottom: '20px' }}>
                                        <div style={{ fontSize: '11px', color: L.textMuted, marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Send this to the bot</div>
                                        <div style={{ fontSize: '18px', fontWeight: 700, color: L.text, fontFamily: 'monospace', marginBottom: '10px', userSelect: 'all' }}>/connect {creatorConnectCode}</div>
                                        <button onClick={copyCode}
                                            style={{ ...signupBtnSecondary, padding: '6px 16px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                            onMouseEnter={e => { e.currentTarget.style.borderColor = L.blue; e.currentTarget.style.color = L.blue; }}
                                            onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.color = L.text; }}>
                                            {copied ? '✓ Copied!' : 'Copy'}
                                        </button>
                                    </div>

                                    <div style={{ background: '#f8fafc', border: `1px solid ${L.border}`, borderRadius: '14px', padding: '16px', marginBottom: '20px', textAlign: 'left' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                            <span style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', color: L.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>1</span>
                                            <span style={{ color: L.text, fontSize: '14px' }}>Open <b>@{BOT_USERNAME}</b> on Telegram</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', color: L.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>2</span>
                                            <span style={{ color: L.text, fontSize: '14px' }}>Send <b>/connect {creatorConnectCode}</b></span>
                                        </div>
                                    </div>

                                    <a href={deepLink} target="_blank" rel="noopener noreferrer"
                                        style={{ ...signupBtnSecondary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', textDecoration: 'none', marginBottom: '16px', fontSize: '13px', padding: '10px 20px', color: L.blue, borderColor: L.blue }}
                                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,174,239,0.06)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>
                                        Connect via Telegram
                                    </a>

                                    <p style={{ fontSize: '13px', color: L.textMuted, animation: 'pulse2 2s ease-in-out infinite', marginBottom: '16px' }}>
                                        Waiting for connection...
                                    </p>
                                </div>
                            )}

                            <button onClick={() => { sessionStorage.setItem('creatorOnboarding', '6'); setRegistrationStep(6); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: L.textMuted, fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", padding: '8px', animation: 'fadeIn 0.5s 0.5s both' }}
                                onMouseEnter={e => { e.currentTarget.style.color = L.text; }}
                                onMouseLeave={e => { e.currentTarget.style.color = L.textMuted; }}>
                                Skip for now
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ---- RENDER: Creator Step 6 — Add Channel ----
    if (!isLogin && registrationStep === 6) {
        const handleAddChannel = async () => {
            setAddingChannelOnboarding(true);
            setError('');
            try {
                await api.post('/api/dashboard/request-add-channel', { token: localStorage.getItem('token') });
            } catch (e) {
                setError(e.response?.data?.error || 'Failed to send. Make sure Telegram is connected.');
                setAddingChannelOnboarding(false);
            }
        };

        return (
            <div style={signupPageWrap}>
                <style>{`
                    @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes pulse2 { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                `}</style>
                <div style={{ ...signupCardSt, maxWidth: '460px', animation: 'fadeSlideUp 0.5s both cubic-bezier(.4,0,.2,1)' }}>

                    {creatorChannelAdded ? (
                        <div style={{ animation: 'fadeSlideUp 0.4s both' }}>
                            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            </div>
                            <h2 style={{ fontSize: '24px', fontWeight: 800, color: L.text, marginBottom: '8px' }}>Channel Added!</h2>
                            <p style={{ color: L.textMuted, fontSize: '14px' }}>Redirecting to your dashboard...</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ animation: 'fadeIn 0.4s both' }}>
                                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(0,174,239,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={L.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>
                                </div>
                                <h2 style={{ fontSize: '24px', fontWeight: 800, color: L.text, marginBottom: '4px' }}>Add Your First Channel</h2>
                                <p style={{ color: L.textMuted, fontSize: '14px', marginBottom: '24px', lineHeight: 1.5 }}>
                                    Connect a Telegram channel to get started
                                </p>
                            </div>

                            {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#ef4444', textAlign: 'center' }}>{error}</div>}

                            {!addingChannelOnboarding ? (
                                <div style={{ animation: 'fadeSlideUp 0.5s 0.1s both' }}>
                                    <button onClick={handleAddChannel}
                                        style={{ ...signupBtn, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = L.blueHover; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = L.blue; e.currentTarget.style.transform = 'translateY(0)'; }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                        Add Channel
                                    </button>
                                </div>
                            ) : (
                                <div style={{ animation: 'fadeSlideUp 0.5s 0.1s both' }}>
                                    <div style={{ background: '#f8fafc', border: `1px solid ${L.border}`, borderRadius: '14px', padding: '16px', marginBottom: '20px', textAlign: 'left' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                            <span style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', color: L.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>1</span>
                                            <span style={{ color: L.text, fontSize: '14px' }}>Open the message from <b>@{BOT_USERNAME}</b></span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                            <span style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', color: L.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>2</span>
                                            <span style={{ color: L.text, fontSize: '14px' }}>Tap <b>"Select Channel"</b> and pick your channel</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', color: L.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>3</span>
                                            <span style={{ color: L.text, fontSize: '14px' }}>The bot will be added automatically</span>
                                        </div>
                                    </div>

                                    <p style={{ fontSize: '13px', color: L.textMuted, animation: 'pulse2 2s ease-in-out infinite', marginBottom: '8px' }}>
                                        Waiting for channel...
                                    </p>
                                    <button onClick={handleAddChannel}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: L.blue, fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", padding: '4px 8px', marginBottom: '12px', opacity: 0.8 }}
                                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.textDecoration = 'underline'; }}
                                        onMouseLeave={e => { e.currentTarget.style.opacity = '0.8'; e.currentTarget.style.textDecoration = 'none'; }}>
                                        Resend message
                                    </button>
                                </div>
                            )}

                            <button onClick={() => { sessionStorage.removeItem('creatorOnboarding'); navigate('/dashboard'); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: L.textMuted, fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", padding: '8px', animation: 'fadeIn 0.5s 0.5s both' }}
                                onMouseEnter={e => { e.currentTarget.style.color = L.text; }}
                                onMouseLeave={e => { e.currentTarget.style.color = L.textMuted; }}>
                                Skip for now
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ---- RENDER: Login ----
    return (
        <div style={pageWrap}>

            <div style={cardSt}>
                <h2 style={{ fontSize: '38px', fontWeight: 700, marginBottom: '30px', color: 'var(--text)' }}>Login</h2>
                {error && <div style={errorBox}>{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div style={inputBox}><input type="text" value={username} onChange={usernameHandler} placeholder="Username" required style={inputStyle} /><span style={inputIcon}><Icons.User /></span></div>
                    <div style={inputBox}>
                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required style={inputStyle} autoComplete="current-password" />
                        <span style={{ ...inputIcon, cursor: 'pointer' }} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <Icons.Eye /> : <Icons.EyeOff />}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '-10px', marginBottom: '16px', fontSize: '14px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text)' }}>
                            <input type="checkbox" style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: 'var(--accent)' }} />
                            Remember me
                        </label>
                        <span onClick={() => setShowForgot(true)} style={{ color: 'var(--text)', cursor: 'pointer' }}>Forgot Password?</span>
                    </div>
                    <button type="submit" disabled={loading} style={{ ...btnPrimary, marginBottom: '20px', opacity: loading ? 0.5 : 1 }}>
                        {loading ? 'Please wait...' : 'Login'}
                    </button>
                </form>
                <GoogleSignInButton onClick={triggerGoogleSignIn} loading={loading} />
                <div style={{ textAlign: 'center', color: 'var(--text)', fontSize: '14px' }}>
                    Don't have an account?{' '}
                    <span onClick={() => { setIsLogin(false); setRegistrationStep(1); setError(''); setPassword(''); setConfirmPassword(''); }} style={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>Sign up</span>
                </div>
            </div>
        </div>
    );
}
